// imports
import { useState, SyntheticEvent }from 'react';
import { PrimaryButton } from '../../PrimaryButton';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import { useDispatch } from 'react-redux';

// local imports
import { setAwdVisibility } from '../../../../store/mapSlice';
import {
  setSelectedEndMonth,
  setSelectedEndYear,
  setSelectedModel,
  setSelectedSector,
  setSelectedStartMonth,
  setSelectedStartYear,
  setSelectedVariable,
  setTemporalResolution,
  setAppliedSector,
  setAppliedModel,
  setAppliedVariable,
  setAppliedTemporalResolution,
  setAppliedStartMonth,
  setAppliedStartYear,
} from '../../../../store/waterDataSlice';
import { uiActions } from '../../../../store';
import { useUrlParameters } from '../../../../hooks/useUrlParameters';
import { monthKey } from '../../../../constants/Months';

// Relevant data from "Available Water Data" menus
interface UpdateMapButtonProps {
  selectedSector: string;
  selectedModel: string | null;
  selectedVariable: string | null;
  selectedTemporalResolution: string;
  selectedStartMonth: string | null;
  selectedStartYear: string | null;
  selectedEndMonth: string | null;
  selectedEndYear: string | null;
  useDateRange: boolean;
  isDateAlertVisible: boolean;
  isDisabled: boolean;
  isMonthly: boolean;
}

export default function UpdateMapButton({
  selectedSector,
  selectedModel,
  selectedVariable,
  selectedTemporalResolution,
  selectedStartMonth,
  selectedStartYear,
  selectedEndMonth,
  selectedEndYear,
  useDateRange,
  isDateAlertVisible,
  isDisabled,
  isMonthly,
}: UpdateMapButtonProps) {
  const dispatch = useDispatch();
  const { setParameter } = useUrlParameters();
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClick = () => {
    const messages = [];

    // If there are errors with the model, variable, or date range, invoke the snackbar alert
    if (isDateAlertVisible) {
      messages.push('Invalid date range.');
    }
    if (
      selectedModel === 'no-models' ||
      selectedModel === 'error' ||
      selectedModel === undefined ||
      selectedModel === '' ||
      selectedModel === null
    ) {
      messages.push('No model selected.');
    }
    if (
      selectedVariable === 'no-variables' ||
      selectedVariable === 'error' ||
      selectedVariable === undefined ||
      selectedVariable === '' ||
      selectedVariable === null
    ) {
      messages.push('No variable selected.');
    }

    // If there are multiple errors, join the error messages
    if (messages.length > 0) {
      setErrorMessage(messages.join(' '));
      setOpen(true);
      return;
    }
    // if no issues with form validation, toggle the AWD visible
    dispatch(uiActions.setSelectedAwdFeature(null));
    dispatch(setSelectedSector(selectedSector));
    dispatch(setSelectedModel(selectedModel));
    dispatch(setSelectedVariable(selectedVariable));
    dispatch(setTemporalResolution(selectedTemporalResolution));
    dispatch(setSelectedStartMonth(isMonthly ? selectedStartMonth : null));
    dispatch(setSelectedStartYear(selectedStartYear));
    dispatch(setSelectedEndMonth(selectedEndMonth));
    dispatch(setSelectedEndYear(selectedEndYear));
    // update applied state
    dispatch(setAppliedSector(selectedSector));
    dispatch(setAppliedModel(selectedModel));
    dispatch(setAppliedVariable(selectedVariable));
    dispatch(setAppliedStartMonth(isMonthly ? selectedStartMonth : null));
    dispatch(setAppliedStartYear(selectedStartYear));
    dispatch(setAppliedTemporalResolution(isMonthly ? 'monthly' : 'annual'));
    dispatch(uiActions.setAwdNoDataProbing(true));
    dispatch(setAwdVisibility(true));

    // Update URL parameters after successful validation
    setParameter('sector', selectedSector);
    if (selectedModel) setParameter('model', selectedModel);
    if (selectedVariable) setParameter('variable', selectedVariable);
    setParameter('temporalResolution', selectedTemporalResolution);
    if (selectedStartYear) setParameter('startYear', selectedStartYear);
    if (selectedStartMonth && selectedTemporalResolution === 'monthly') {
      setParameter('startMonth', monthKey(selectedStartMonth));
    }
    setParameter('useDateRange', useDateRange.toString());
    if (useDateRange) {
      if (selectedEndYear) setParameter('endYear', selectedEndYear);
      if (selectedEndMonth && selectedTemporalResolution === 'monthly') {
        setParameter('endMonth', monthKey(selectedEndMonth));
      }
    }
  };

  // For closing the snackbar alert
  const handleClose = (
    _event?: SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <div>
      <Tooltip
        title={isDisabled ? 'No changes to apply' : ''}
        arrow
        disableHoverListener={!isDisabled}
      >
        <div>
          <PrimaryButton
            onClick={handleClick}
            variant="contained"
            fullWidth
            disabled={isDisabled}
            aria-label="Update Map"
          >
            Update Map
          </PrimaryButton>
        </div>
      </Tooltip>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
